import { useState, useMemo, useEffect, useCallback } from 'react';
import { IcGrid, IcList } from '../../components/ui/Icons';
import PageHeader from '../../components/ui/PageHeader';
import Badge, { UrgenciaBadge } from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import useAppStore from '../../store/app.store';

const PRIORIDADE_COR = { baixa: 'verde', media: 'azul', alta: 'laranja', urgente: 'vermelho' };
const URGENCIA_BORDA = { verde: 'border-l-green-500', amarelo: 'border-l-yellow-400', laranja: 'border-l-orange-500', vermelho: 'border-l-red-600' };

function DemandaClienteCard({ demanda: d, onAceitar, onRecusar }) {
  const PRIOR = { alta: 'text-red-600 bg-red-50', media: 'text-yellow-700 bg-yellow-50', baixa: 'text-gray-600 bg-gray-100' };
  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-l-indigo-400 mb-2">
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-xs font-semibold text-gray-800 leading-tight flex-1">{d.titulo}</p>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${PRIOR[d.prioridade]}`}>{d.prioridade}</span>
      </div>
      {d.cliente_nome && <p className="text-[11px] text-indigo-600 font-medium mb-1">{d.cliente_nome}</p>}
      {d.solicitante && <p className="text-[10px] text-gray-400 mb-1">Por: {d.solicitante}</p>}
      <p className="text-xs text-gray-500 line-clamp-2 mb-2">{d.descricao}</p>
      <div className="flex gap-1.5">
        <button onClick={onAceitar}
          className="flex-1 text-[11px] py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-semibold transition-colors">
          ✓ Aceitar
        </button>
        <button onClick={onRecusar}
          className="flex-1 text-[11px] py-1 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 font-medium transition-colors">
          ✕ Recusar
        </button>
      </div>
    </div>
  );
}

function ChamadoCard({ ch, onClick, onDragStart }) {
  const cor = ch.cor_urgencia || 'verde';
  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('chamadoId', ch.id); onDragStart(ch.id); }}
      onClick={() => onClick(ch)}
      className={`bg-white rounded-lg p-4 shadow-sm border-l-4 ${URGENCIA_BORDA[cor]} cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 flex-1 line-clamp-2">{ch.titulo}</h3>
        <UrgenciaBadge cor={cor} />
      </div>
      <p className="text-xs text-gray-500 mt-1">{ch.cliente_nome || 'Sem cliente'}</p>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <Badge status={ch.status}>{ch.status.replace(/_/g, ' ')}</Badge>
        <Badge variant={PRIORIDADE_COR[ch.prioridade]}>{ch.prioridade}</Badge>
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-gray-400">{ch.responsavel_nome || 'Não atribuído'}</span>
        <span className="text-xs text-gray-400">
          {ch.data_prazo ? formatDistanceToNow(new Date(ch.data_prazo), { locale: ptBR, addSuffix: true }) : '—'}
        </span>
      </div>
    </div>
  );
}

export default function Chamados() {
  const usuario = useAppStore(s => s.usuario);
  const isAdmin = usuario?.nivel_acesso <= 1;
  const [chamados, setChamados] = useState([]);
  const [chamadosColaboradores, setChamadosColaboradores] = useState([]);
  const [abaAdmin, setAbaAdmin] = useState('meus'); // 'meus' | 'colaboradores'
  const [demandasCliente, setDemandasCliente] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [filtros, setFiltros] = useState({ status: '', setor: '', prioridade: '' });
  const [visao, setVisao] = useState('kanban');
  const [modalNovo, setModalNovo] = useState(false);
  const [modalLinks, setModalLinks] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [buscaLink, setBuscaLink] = useState('');

  const carregarChamados = useCallback(async () => {
    try {
      setLoading(true);
      if (isAdmin) {
        // Admin: busca os próprios e os dos colaboradores separadamente
        const [meus, colab] = await Promise.all([
          api.get('/chamados', { params: { limit: 200, responsavel_id: usuario.id } }),
          api.get('/chamados', { params: { limit: 200, excluir_responsavel: usuario.id } }),
        ]);
        setChamados(meus.data.data || []);
        setChamadosColaboradores(colab.data.data || []);
      } else {
        const { data } = await api.get('/chamados', { params: { limit: 200 } });
        setChamados(data.data || []);
      }
    } catch {
      toast.error('Erro ao carregar chamados.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, usuario?.id]);

  const carregarDemandasCliente = useCallback(async () => {
    try {
      const { data } = await api.get('/midias/demandas-extras', { params: { status: 'pendente' } });
      setDemandasCliente(data || []);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    carregarChamados();
    carregarDemandasCliente();
    api.get('/clientes/lista').then(r => setClientes(r.data || [])).catch(() => toast.error('Erro ao carregar clientes.'));
    api.get('/usuarios/equipe').then(r => setUsuarios(r.data || [])).catch(() => {});
  }, [carregarChamados, carregarDemandasCliente]);

  const addChamado = async (form) => {
    setSalvando(true);
    try {
      await api.post('/chamados', form);
      toast.success('Chamado criado!');
      setModalNovo(false);
      carregarChamados();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao criar chamado.');
    } finally {
      setSalvando(false);
    }
  };

  const listaAtiva = isAdmin && abaAdmin === 'colaboradores' ? chamadosColaboradores : chamados;

  const filtrados = useMemo(() =>
    listaAtiva.filter(ch => {
      const s = !filtros.status || ch.status === filtros.status;
      const se = !filtros.setor || ch.setor === filtros.setor;
      const p = !filtros.prioridade || ch.prioridade === filtros.prioridade;
      return s && se && p;
    }),
    [listaAtiva, filtros]
  );

  const handleDrop = async (novoStatus) => {
    if (!draggingId || !novoStatus) return;
    const ch = chamados.find(c => c.id === draggingId);
    if (!ch || ch.status === novoStatus) { setDraggingId(null); setDragOverCol(null); return; }
    setChamados(prev => prev.map(c => c.id === draggingId ? { ...c, status: novoStatus } : c));
    setDraggingId(null);
    setDragOverCol(null);
    try {
      await api.patch(`/chamados/${draggingId}/status`, { status: novoStatus });
    } catch {
      toast.error('Erro ao mover chamado.');
      carregarChamados();
    }
  };

  const grupos = { pendente: [], em_andamento: [], aguardando_aprovacao: [], concluido: [] };
  filtrados.forEach(ch => { if (grupos[ch.status]) grupos[ch.status].push(ch); });

  return (
    <div>
      <PageHeader
        title="Chamados"
        subtitle={loading ? 'Carregando...' : 'Gestão de demandas com controle de prazos'}
        actions={
          <>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {['kanban', 'lista'].map(v => (
                <button key={v} onClick={() => setVisao(v)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${visao === v ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                  {v === 'kanban' ? 'Kanban' : 'Lista'}
                </button>
              ))}
            </div>
            <button onClick={() => setModalLinks(true)} className="btn-secondary flex items-center gap-1.5">
              🔗 <span>Links Clientes</span>
            </button>
            <button onClick={() => setModalNovo(true)} className="btn-primary">+ Novo Chamado</button>
          </>
        }
      />

      {isAdmin && (
        <div className="flex gap-1 mb-5" style={{ borderBottom: '1px solid #2A3652' }}>
          {[
            { k: 'meus', l: `Minhas Demandas (${chamados.length})` },
            { k: 'colaboradores', l: `Colaboradores (${chamadosColaboradores.length})` },
          ].map(t => (
            <button key={t.k} onClick={() => setAbaAdmin(t.k)} style={{
              padding: '8px 18px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer',
              color: abaAdmin === t.k ? '#6C4CF5' : '#94A3B8',
              fontWeight: abaAdmin === t.k ? 700 : 400,
              borderBottom: abaAdmin === t.k ? '2px solid #6C4CF5' : '2px solid transparent',
              marginBottom: -1,
            }}>{t.l}</button>
          ))}
        </div>
      )}

      <div className="flex gap-3 mb-6 flex-wrap">
        {[
          { key: 'status', placeholder: 'Todos status', options: [['pendente','Pendente'],['em_andamento','Em Andamento'],['aguardando_aprovacao','Ag. Aprovação'],['concluido','Concluído'],['cancelado','Cancelado']] },
          { key: 'setor', placeholder: 'Todos setores', options: [['midias','Mídias'],['trafego','Tráfego'],['comercial','Comercial'],['financeiro','Financeiro'],['administrativo','Administrativo']] },
          { key: 'prioridade', placeholder: 'Todas prioridades', options: [['baixa','Baixa'],['media','Média'],['alta','Alta'],['urgente','Urgente']] },
        ].map(f => (
          <select key={f.key} className="form-input w-auto text-sm" value={filtros[f.key]}
            onChange={e => setFiltros(p => ({ ...p, [f.key]: e.target.value }))}>
            <option value="">{f.placeholder}</option>
            {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
      </div>

      {visao === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {/* Coluna especial: demandas enviadas pelos clientes */}
          <div className="kanban-col min-h-[200px]" style={{ borderTop: '3px solid #6366f1' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <span className="text-sm">📥</span>
                <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6366f1' }}>Demanda de Cliente</h3>
              </div>
              <span className="text-xs rounded-full px-2 py-0.5 font-semibold" style={{ background: '#ede9fe', color: '#6366f1' }}>{demandasCliente.length}</span>
            </div>
            {demandasCliente.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6">Nenhuma demanda pendente.</p>
            )}
            {demandasCliente.map(d => (
              <DemandaClienteCard key={d.id} demanda={d}
                onAceitar={async () => {
                  try {
                    // Cria chamado a partir da demanda do cliente
                    const { data: novoChamado } = await api.post('/chamados', {
                      titulo: d.titulo,
                      descricao: `${d.descricao}${d.solicitante ? `\n\nSolicitado por: ${d.solicitante}` : ''}`,
                      cliente_id: d.cliente_id,
                      setor: 'midias',
                      prioridade: d.prioridade || 'media',
                      prazo_horas: 48,
                    });
                    // Marca a demanda como aceita
                    await api.patch(`/midias/demandas-extras/${d.id}`, { status: 'em_andamento' });
                    setDemandasCliente(prev => prev.filter(x => x.id !== d.id));
                    setChamados(prev => [novoChamado, ...prev]);
                    toast.success('Demanda aceita! Chamado criado em Pendente.');
                  } catch (err) { toast.error(err.response?.data?.error || 'Erro ao aceitar.'); }
                }}
                onRecusar={async () => {
                  try {
                    await api.patch(`/midias/demandas-extras/${d.id}`, { status: 'concluido' });
                    setDemandasCliente(prev => prev.filter(x => x.id !== d.id));
                    toast.success('Demanda recusada.');
                  } catch { toast.error('Erro.'); }
                }}
              />
            ))}
          </div>

          {[
            { key: 'pendente', label: 'Pendente' },
            { key: 'em_andamento', label: 'Em Andamento' },
            { key: 'aguardando_aprovacao', label: 'Ag. Aprovação' },
            { key: 'concluido', label: 'Concluído' },
          ].map(col => (
            <div key={col.key}
              className={`kanban-col min-h-[200px] transition-colors ${dragOverCol === col.key ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset rounded-xl' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOverCol(col.key); }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={e => { e.preventDefault(); handleDrop(col.key); }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{col.label}</h3>
                <span className="text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">{grupos[col.key]?.length || 0}</span>
              </div>
              {(grupos[col.key] || []).map(ch => (
                <ChamadoCard key={ch.id} ch={ch} onClick={setDetalhe} onDragStart={setDraggingId} />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr><th>Título</th><th>Cliente</th><th>Setor</th><th>Status</th><th>Prioridade</th><th>Prazo</th><th>Urgência</th></tr>
              </thead>
              <tbody>
                {filtrados.map(ch => (
                  <tr key={ch.id} onClick={() => setDetalhe(ch)} className="cursor-pointer">
                    <td className="font-medium text-gray-900 max-w-xs truncate">{ch.titulo}</td>
                    <td className="text-gray-500 text-xs">{ch.cliente_nome || '—'}</td>
                    <td><Badge variant="azul">{ch.setor}</Badge></td>
                    <td><Badge status={ch.status}>{ch.status.replace(/_/g,' ')}</Badge></td>
                    <td><Badge variant={PRIORIDADE_COR[ch.prioridade]}>{ch.prioridade}</Badge></td>
                    <td className="text-xs text-gray-500">{ch.data_prazo ? format(new Date(ch.data_prazo), 'dd/MM/yy HH:mm') : '—'}</td>
                    <td><UrgenciaBadge cor={ch.cor_urgencia || 'verde'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtrados.length && <p className="text-center text-gray-400 py-12 text-sm">Nenhum chamado encontrado.</p>}
          </div>
        </div>
      )}

      <Modal open={modalNovo} onClose={() => setModalNovo(false)} title="Novo Chamado">
        <FormNovoChamado salvando={salvando} onSalvar={addChamado} onFechar={() => setModalNovo(false)} />
      </Modal>

      <Modal open={!!detalhe} onClose={() => { setDetalhe(null); carregarChamados(); }} title={detalhe?.titulo} size="lg">
        {detalhe && <DetalheChamado chamadoId={detalhe.id} onFechar={() => { setDetalhe(null); carregarChamados(); }} />}
      </Modal>

      <Modal open={modalLinks} onClose={() => { setModalLinks(false); setBuscaLink(''); }} title="🔗 Links de solicitação para clientes">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">Copie e envie o link para o cliente. Ele poderá abrir demandas extras sem precisar de login.</p>
          <input
            className="form-input"
            placeholder="Buscar cliente..."
            value={buscaLink}
            onChange={e => setBuscaLink(e.target.value)}
          />
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {clientes
              .filter(c => {
                const nome = (c.nome_fantasia || c.razao_social || '').toLowerCase();
                return nome.includes(buscaLink.toLowerCase());
              })
              .map(c => {
                const nome = c.nome_fantasia || c.razao_social;
                const link = `${window.location.origin}/solicitar/${c.id}`;
                return (
                  <div key={c.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{nome}</p>
                      <p className="text-xs text-gray-400 truncate">{link}</p>
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(link); toast.success(`Link de ${nome} copiado!`); }}
                      className="btn-secondary text-xs px-3 py-1.5 flex-shrink-0 flex items-center gap-1">
                      📋 Copiar
                    </button>
                  </div>
                );
              })}
            {!clientes.length && <p className="text-center text-gray-400 py-6 text-sm">Nenhum cliente cadastrado.</p>}
          </div>
        </div>
      </Modal>
    </div>
  );
}

function FormNovoChamado({ onSalvar, onFechar, salvando }) {
  const defaultPrazo = () => { const d = new Date(); d.setDate(d.getDate() + 2); d.setSeconds(0, 0); return d.toISOString().slice(0, 16); };
  const [form, setForm] = useState({ titulo: '', descricao: '', setor: 'midias', prioridade: 'media', data_prazo: defaultPrazo(), cliente_id: '', responsavel_id: '' });
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/clientes/lista')
      .then(r => setClientes(r.data || []))
      .catch(err => toast.error('Clientes: ' + (err.response?.status || err.message)));
    api.get('/usuarios/equipe')
      .then(r => setUsuarios(r.data || []))
      .catch(() => {});
  }, []);
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      <div><label className="form-label">Título *</label><input className="form-input" value={form.titulo} onChange={e => set('titulo', e.target.value)} required /></div>
      <div><label className="form-label">Descrição</label><textarea className="form-input" rows={3} value={form.descricao} onChange={e => set('descricao', e.target.value)} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="form-label">Cliente</label>
          <select className="form-input" value={form.cliente_id} onChange={e => set('cliente_id', e.target.value)}>
            <option value="">Sem cliente</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Responsável</label>
          <select className="form-input" value={form.responsavel_id} onChange={e => set('responsavel_id', e.target.value)}>
            <option value="">Não atribuído</option>
            {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Setor</label>
          <select className="form-input" value={form.setor} onChange={e => set('setor', e.target.value)}>
            <option value="midias">Mídias</option><option value="trafego">Tráfego</option>
            <option value="comercial">Comercial</option><option value="financeiro">Financeiro</option>
            <option value="administrativo">Administrativo</option>
          </select>
        </div>
        <div>
          <label className="form-label">Prioridade</label>
          <select className="form-input" value={form.prioridade} onChange={e => set('prioridade', e.target.value)}>
            <option value="baixa">Baixa</option><option value="media">Média</option>
            <option value="alta">Alta</option><option value="urgente">Urgente</option>
          </select>
        </div>
        <div><label className="form-label">Prazo</label><input type="datetime-local" className="form-input" value={form.data_prazo} onChange={e => set('data_prazo', e.target.value)} required /></div>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={salvando} className="btn-primary flex-1">{salvando ? 'Criando...' : 'Criar Chamado'}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

function DetalheChamado({ chamadoId, onFechar }) {
  const usuario = useAppStore(s => s.usuario);
  const [ch, setCh] = useState(null);
  const [texto, setTexto] = useState('');
  const [novoStatus, setNovoStatus] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [abaDetalhe, setAbaDetalhe] = useState('detalhes');
  const [checklist, setChecklist] = useState([]);
  const [novoItem, setNovoItem] = useState('');
  const [horas, setHoras] = useState([]);
  const [novaHora, setNovaHora] = useState({ minutos: 60, descricao: '', data: new Date().toISOString().split('T')[0] });

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get(`/chamados/${chamadoId}`);
      setCh(data);
    } catch {
      toast.error('Erro ao carregar chamado.');
    }
  }, [chamadoId]);

  useEffect(() => {
    carregar();
    api.get(`/checklist/${chamadoId}`).then(r => setChecklist(r.data || [])).catch(() => {});
    api.get(`/horas/chamado/${chamadoId}`).then(r => setHoras(r.data || [])).catch(() => {});
  }, [carregar, chamadoId]);

  const adicionarItem = async () => {
    if (!novoItem.trim()) return;
    const { data } = await api.post(`/checklist/${chamadoId}`, { texto: novoItem.trim() });
    setChecklist(p => [...p, data]);
    setNovoItem('');
  };

  const [editandoPrazo, setEditandoPrazo] = useState(false);
  const [novoPrazo, setNovoPrazo] = useState('');

  const salvarPrazo = async () => {
    if (!novoPrazo) return;
    try {
      await api.patch(`/chamados/${chamadoId}/prazo`, { data_prazo: novoPrazo });
      toast.success('Prazo atualizado!');
      setEditandoPrazo(false);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao atualizar o prazo.');
    }
  };

  const toggleItem = async (item) => {
    const { data } = await api.patch(`/checklist/item/${item.id}`, { concluido: !item.concluido });
    setChecklist(p => p.map(x => x.id === item.id ? data : x));
  };

  const removerItem = async (itemId) => {
    await api.delete(`/checklist/item/${itemId}`);
    setChecklist(p => p.filter(x => x.id !== itemId));
  };

  const adicionarHora = async () => {
    if (!novaHora.minutos) return;
    const { data } = await api.post(`/horas/chamado/${chamadoId}`, novaHora);
    setHoras(p => [...p, { ...data, usuario_nome: usuario?.nome }]);
    setNovaHora({ minutos: 60, descricao: '', data: new Date().toISOString().split('T')[0] });
  };

  const removerHora = async (id) => {
    await api.delete(`/horas/${id}`);
    setHoras(p => p.filter(x => x.id !== id));
  };

  const totalHoras = horas.reduce((s, h) => s + parseInt(h.minutos || 0), 0);
  const fmtHoras = (min) => `${Math.floor(min / 60)}h ${min % 60}min`;

  const registrar = async (e) => {
    e.preventDefault();
    if (!texto.trim()) return;
    setSalvando(true);
    try {
      await api.post(`/chamados/${chamadoId}/atualizacoes`, { texto, status_novo: novoStatus || undefined });
      setTexto('');
      setNovoStatus('');
      toast.success('Registrado!');
      carregar();
    } catch {
      toast.error('Erro ao registrar atualização.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (!window.confirm('Excluir este chamado permanentemente?')) return;
    setExcluindo(true);
    try {
      await api.delete(`/chamados/${chamadoId}`);
      toast.success('Chamado excluído.');
      onFechar();
    } catch {
      toast.error('Erro ao excluir chamado.');
      setExcluindo(false);
    }
  };

  if (!ch) return <p className="text-sm text-gray-400 py-8 text-center">Carregando...</p>;

  const cor = ch.cor_urgencia || 'verde';

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-gray-500">Setor:</span> {ch.setor}</div>
        <div><span className="text-gray-500">Prioridade:</span> <Badge variant={PRIORIDADE_COR[ch.prioridade]}>{ch.prioridade}</Badge></div>
        <div><span className="text-gray-500">Status:</span> <Badge status={ch.status}>{ch.status.replace(/_/g,' ')}</Badge></div>
        <div><span className="text-gray-500">Urgência:</span> <UrgenciaBadge cor={cor} /></div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-gray-500">Prazo:</span>
          {editandoPrazo ? (
            <>
              <input type="datetime-local" className="form-input w-auto" style={{ padding: '3px 8px', fontSize: 13 }}
                value={novoPrazo} onChange={e => setNovoPrazo(e.target.value)} />
              <button className="text-xs font-semibold text-violet-600 hover:text-violet-800" onClick={salvarPrazo}>Salvar</button>
              <button className="text-xs text-gray-400 hover:text-gray-600" onClick={() => setEditandoPrazo(false)}>Cancelar</button>
            </>
          ) : (
            <>
              {ch.data_prazo ? format(new Date(ch.data_prazo), 'dd/MM/yyyy HH:mm') : '—'}
              <button title="Editar prazo" className="text-gray-400 hover:text-violet-600"
                onClick={() => {
                  const d = ch.data_prazo ? new Date(ch.data_prazo) : new Date();
                  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
                  setNovoPrazo(d.toISOString().slice(0, 16));
                  setEditandoPrazo(true);
                }}>✏️</button>
            </>
          )}
        </div>
        {ch.data_conclusao && <div><span className="text-gray-500">Concluído:</span> {format(new Date(ch.data_conclusao), 'dd/MM/yyyy HH:mm')}</div>}
      </div>
      {ch.descricao && <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">{ch.descricao}</div>}

      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Histórico</h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {(ch.atualizacoes || []).map(a => (
            <div key={a.id} className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between mb-1">
                <span className="text-xs font-medium text-gray-700">{a.autor_nome}</span>
                <span className="text-xs text-gray-400">{format(new Date(a.criado_em), 'dd/MM HH:mm')}</span>
              </div>
              <p className="text-sm text-gray-600">{a.texto}</p>
              {a.status_novo && <Badge status={a.status_novo} className="mt-1">{a.status_novo.replace(/_/g,' ')}</Badge>}
            </div>
          ))}
          {!(ch.atualizacoes || []).length && <p className="text-xs text-gray-400">Nenhuma atualização.</p>}
        </div>
      </div>

      {!['concluido', 'cancelado'].includes(ch.status) && (
        <form onSubmit={registrar} className="space-y-3 border-t pt-4">
          <textarea className="form-input" rows={2} placeholder="Registrar atualização..." value={texto} onChange={e => setTexto(e.target.value)} required />
          <div className="flex gap-2">
            <select className="form-input w-auto text-sm" value={novoStatus} onChange={e => setNovoStatus(e.target.value)}>
              <option value="">Manter status</option>
              <option value="em_andamento">Em Andamento</option>
              <option value="aguardando_aprovacao">Ag. Aprovação</option>
              <option value="concluido">Concluído</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <button type="submit" disabled={salvando} className="btn-primary">{salvando ? 'Salvando...' : 'Registrar'}</button>
          </div>
        </form>
      )}

      <div style={{ marginTop: 20, borderTop: '1px solid #243356', paddingTop: 16 }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
          {[['detalhes', '📋 Detalhes'], ['checklist', '✅ Checklist'], ['horas', '⏱ Horas']].map(([k, l]) => (
            <button key={k} onClick={() => setAbaDetalhe(k)}
              style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: abaDetalhe === k ? '#6C4CF5' : '#1A2744', color: abaDetalhe === k ? '#fff' : '#64748B' }}>
              {l}
            </button>
          ))}
        </div>

        {abaDetalhe === 'checklist' && (
          <div>
            {checklist.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ height: 4, background: '#243356', borderRadius: 999, marginBottom: 8 }}>
                  <div style={{ height: '100%', width: `${Math.round((checklist.filter(x => x.concluido).length / checklist.length) * 100)}%`, background: '#22C55E', borderRadius: 999 }} />
                </div>
                <p style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
                  {checklist.filter(x => x.concluido).length}/{checklist.length} concluídos
                </p>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {checklist.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#131D31', borderRadius: 8, padding: '8px 12px' }}>
                  <input type="checkbox" checked={item.concluido} onChange={() => toggleItem(item)}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#22C55E', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, color: item.concluido ? '#64748B' : '#E2E8F0', textDecoration: item.concluido ? 'line-through' : 'none' }}>{item.texto}</span>
                  <button onClick={() => removerItem(item.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>×</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={novoItem} onChange={e => setNovoItem(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && adicionarItem()}
                placeholder="Adicionar item ao checklist..."
                style={{ flex: 1, background: '#131D31', border: '1px solid #243356', borderRadius: 8, padding: '8px 12px', color: '#E2E8F0', fontSize: 13 }} />
              <button onClick={adicionarItem} style={{ background: '#6C4CF5', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>+ Adicionar</button>
            </div>
          </div>
        )}

        {abaDetalhe === 'horas' && (
          <div>
            {totalHoras > 0 && (
              <div style={{ background: '#131D31', borderRadius: 10, padding: '12px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>Total registrado</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#6C4CF5' }}>{fmtHoras(totalHoras)}</span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {horas.map(h => (
                <div key={h.id} style={{ background: '#131D31', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 12, color: '#E2E8F0', fontWeight: 600 }}>{fmtHoras(parseInt(h.minutos))}</p>
                    {h.descricao && <p style={{ fontSize: 11, color: '#64748B' }}>{h.descricao}</p>}
                    <p style={{ fontSize: 10, color: '#4A5A78' }}>{h.usuario_nome} · {new Date(h.data).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <button onClick={() => removerHora(h.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>×</button>
                </div>
              ))}
              {horas.length === 0 && <p style={{ fontSize: 12, color: '#64748B', textAlign: 'center', padding: 12 }}>Nenhum registro de horas.</p>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8, marginBottom: 8 }}>
              <div>
                <label style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 4 }}>Minutos</label>
                <input type="number" min="1" value={novaHora.minutos} onChange={e => setNovaHora(p => ({ ...p, minutos: e.target.value }))}
                  style={{ width: '100%', background: '#131D31', border: '1px solid #243356', borderRadius: 8, padding: '8px 10px', color: '#E2E8F0', fontSize: 13 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#64748B', display: 'block', marginBottom: 4 }}>Descrição</label>
                <input value={novaHora.descricao} onChange={e => setNovaHora(p => ({ ...p, descricao: e.target.value }))}
                  placeholder="O que foi feito..."
                  style={{ width: '100%', background: '#131D31', border: '1px solid #243356', borderRadius: 8, padding: '8px 12px', color: '#E2E8F0', fontSize: 13 }} />
              </div>
            </div>
            <button onClick={adicionarHora}
              style={{ width: '100%', background: '#6C4CF5', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 0', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              + Registrar {novaHora.minutos ? fmtHoras(parseInt(novaHora.minutos)) : 'horas'}
            </button>
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <button onClick={excluir} disabled={excluindo}
          className="w-full btn-secondary text-red-500 border-red-200 hover:bg-red-50 hover:border-red-400">
          {excluindo ? 'Excluindo...' : 'Excluir chamado'}
        </button>
      </div>
    </div>
  );
}
